/**
 * React binding for `createSyncQueue` - the queue every mark edit is written
 * through. The queue itself is pure and lives in `syncQueue.js`; this file adds
 * only the three things it cannot do on its own: re-render the indicator when
 * the summary changes, flush on unmount, and guard the tab from closing on
 * unsaved work.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { IDLE_STATUS, SYNC_DEBOUNCE_MS, createSyncQueue } from "./syncQueue.js";

export { SYNC_DEBOUNCE_MS };

export function useSyncQueue(options) {
  const [status, setStatus] = useState(IDLE_STATUS);

  /* Read at call time so a caller may pass an inline object without rebuilding
   * the queue - and losing everything queued in it - on every render. */
  const optionsRef = useRef(options);
  optionsRef.current = options;

  /* One queue for the life of the sheet. `setStatus` is stable, and the queue
   * only reports a summary that actually changed, so a burst of keystrokes
   * re-renders the indicator once rather than per keystroke. */
  const queue = useMemo(
    () => createSyncQueue({ ...optionsRef.current, onStatus: setStatus }),
    [],
  );

  /* Navigating away inside the SPA unmounts the sheet, and a debounced edit
   * would die with it. Fire them: the responses land nowhere, but the writes
   * reach the server. */
  useEffect(() => () => queue.flushNow(), [queue]);

  /* Closing the tab is the one case JavaScript cannot rescue - an in-flight or
   * still-queued write dies with the page. This is the same promise the report
   * card editor makes: never let the teacher leave believing marks are saved
   * when they are not. */
  useEffect(() => {
    if (status.state === "saved") return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [status.state]);

  return {
    status,
    enqueue: queue.enqueue,
    cancel: queue.cancel,
    retryFailed: queue.retryFailed,
  };
}
